import Logger from '@/utils/logger'
import { 
  getConfigList as apiGetConfigList,
  getConfigsByKeys as apiGetConfigsByKeys,
  createConfig as apiCreateConfig,
  updateConfig as apiUpdateConfig,
  deleteConfig as apiDeleteConfig
} from '@/api/configs'

// 默认地区选项常量
const DEFAULT_PROVINCES = ['北京市', '上海市', '广东省', '河北省']
const DEFAULT_CITIES = ['北京市', '上海市', '广州市', '石家庄市']
const DEFAULT_DISTRICTS = ['朝阳区', '海淀区', '西城区', '东城区']

// 默认房屋类型选项（包含"不限"）
const DEFAULT_HOUSE_TYPES = ['不限', '住宅小区', '商务公寓', '写字楼', '商铺']

// 默认户型选项
const DEFAULT_LAYOUTS = ['全部', '1室', '1室1厅', '2室1厅', '3室2厅']

// 辅助函数：解析配置值
function parseConfigValue(configValue, defaultValue) {
  try {
    const parsed = JSON.parse(configValue)
    // 检查是否为非空数组，且所有元素都是字符串
    if (Array.isArray(parsed) && 
        parsed.length > 0 && 
        parsed.every(item => typeof item === 'string')) {
      return parsed
    }
    return defaultValue
  } catch (error) {
    console.error('配置值解析失败:', configValue, error)
    return defaultValue
  }
}

// 辅助函数：格式化选项（通用）
function formatOptions(options, includeAll = true) {
  const formatted = options.map(opt => ({
    label: opt,
    value: opt
  }))
  
  if (includeAll) {
    // 添加"全部"选项
    return [
      { label: '全部', value: '' },
      ...formatted
    ]
  }
  
  return formatted
}

const state = {
  configList: [],
  currentConfig: null,
  loading: false,
  pagination: {
    page: 1,
    size: 20,
    total: 0
  },
  locationOptions: {
    provinces: [],
    cities: [],
    districts: []
  },
  houseTypes: [], // 房屋类型选项
  layouts: [] // 户型选项
}

const mutations = {
  SET_CONFIG_LIST(state, { list, pagination }) {
    state.configList = list
    if (pagination) {
      state.pagination = { ...state.pagination, ...pagination }
    }
  },
  SET_CURRENT_CONFIG(state, config) {
    state.currentConfig = config
  },
  SET_LOADING(state, loading) {
    state.loading = loading
  },
  ADD_CONFIG(state, config) {
    state.configList.unshift(config)
  },
  UPDATE_CONFIG_IN_LIST(state, updatedConfig) {
    const index = state.configList.findIndex(config => config.id === updatedConfig.id)
    if (index !== -1) {
      state.configList.splice(index, 1, { ...state.configList[index], ...updatedConfig })
    }
  },
  REMOVE_CONFIG_FROM_LIST(state, configId) {
    const index = state.configList.findIndex(config => config.id === configId)
    if (index !== -1) {
      state.configList.splice(index, 1)
    }
  },
  SET_LOCATION_OPTIONS(state, { type, options }) {
    if (Object.prototype.hasOwnProperty.call(state.locationOptions, type)) {
      state.locationOptions[type] = options
    }
  },
  SET_HOUSE_TYPES(state, types) {
    state.houseTypes = types
  },
  SET_LAYOUTS(state, layouts) {
    state.layouts = layouts
  }
}

const actions = {
  async fetchConfigs({ commit }, params = {}) {
    commit('SET_LOADING', true)
    try {
      const response = await apiGetConfigList(params)
      
      // 检查响应格式 - 使用 code: 200 表示成功
      if (response.code === 200 && response.data) {
        commit('SET_CONFIG_LIST', {
          list: response.data.list,
          pagination: {
            page: response.data.page,
            size: response.data.size,
            total: response.data.total
          }
        })
        
        // 返回一个 success 格式以保持与其他代码的一致性
        return { success: true, ...response }
      } else {
        console.error('获取配置列表失败:', response)
        throw new Error(response.msg || '获取配置列表失败')
      }
    } catch (error) {
      console.error('获取配置列表失败:', error)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },
  
  async fetchLocationOptions({ commit }) {
    try {
      console.log('开始获取地区配置选项...')
      
      // 获取地区相关配置
      const response = await apiGetConfigList({
        page: 1,
        size: 100
      })
      
      if (!response.success || !response.data || !response.data.list) {
        console.warn('地区配置获取失败，使用默认选项')
        commit('SET_LOCATION_OPTIONS', { type: 'provinces', options: DEFAULT_PROVINCES })
        commit('SET_LOCATION_OPTIONS', { type: 'cities', options: DEFAULT_CITIES })
        commit('SET_LOCATION_OPTIONS', { type: 'districts', options: DEFAULT_DISTRICTS })
        return { success: true, usingDefaults: true }
      }
      
      const configList = response.data.list
      
      // 查找并解析省份配置
      const provinceConfig = configList.find(c => c.configKey === 'location_provinces' && c.isActive)
      const provinces = provinceConfig 
        ? parseConfigValue(provinceConfig.configValue, DEFAULT_PROVINCES)
        : DEFAULT_PROVINCES
      commit('SET_LOCATION_OPTIONS', { type: 'provinces', options: provinces })
      
      // 查找并解析城市配置
      const cityConfig = configList.find(c => c.configKey === 'location_cities' && c.isActive)
      const cities = cityConfig 
        ? parseConfigValue(cityConfig.configValue, DEFAULT_CITIES)
        : DEFAULT_CITIES
      commit('SET_LOCATION_OPTIONS', { type: 'cities', options: cities })
      
      // 查找并解析区县配置
      const districtConfig = configList.find(c => c.configKey === 'location_districts' && c.isActive)
      const districts = districtConfig 
        ? parseConfigValue(districtConfig.configValue, DEFAULT_DISTRICTS)
        : DEFAULT_DISTRICTS
      commit('SET_LOCATION_OPTIONS', { type: 'districts', options: districts })
      
      console.log('地区配置加载成功:', { 
        provinces: provinces.length, 
        cities: cities.length, 
        districts: districts.length 
      })
      
      return { success: true, usingDefaults: false }
    } catch (error) {
      console.error('获取地区配置失败，使用默认选项:', error)
      
      // 使用默认值
      commit('SET_LOCATION_OPTIONS', { type: 'provinces', options: DEFAULT_PROVINCES })
      commit('SET_LOCATION_OPTIONS', { type: 'cities', options: DEFAULT_CITIES })
      commit('SET_LOCATION_OPTIONS', { type: 'districts', options: DEFAULT_DISTRICTS })
      
      return { success: true, usingDefaults: true }
    }
  },
  
  async fetchHouseConfigs({ commit }) {
    try {
      console.log('开始获取房屋配置选项...')
      
      const response = await apiGetConfigsByKeys('type_options,layout_options')
      
      if (response.code !== 200 || !response.data) {
        console.warn('房屋配置获取失败，使用默认选项')
        commit('SET_HOUSE_TYPES', DEFAULT_HOUSE_TYPES)
        commit('SET_LAYOUTS', DEFAULT_LAYOUTS)
        return { success: true, usingDefaults: true }
      }
      
      // 处理房屋类型配置
      if (response.data.type_options) {
        const typeOptions = parseConfigValue(response.data.type_options, DEFAULT_HOUSE_TYPES)
        commit('SET_HOUSE_TYPES', typeOptions)
        console.log('房屋类型配置加载成功:', typeOptions)
      } else {
        console.warn('房屋类型配置不存在，使用默认选项')
        commit('SET_HOUSE_TYPES', DEFAULT_HOUSE_TYPES)
      }
      
      // 处理户型配置
      if (response.data.layout_options) {
        const layoutOptions = parseConfigValue(response.data.layout_options, DEFAULT_LAYOUTS)
        commit('SET_LAYOUTS', layoutOptions)
        console.log('户型配置加载成功:', layoutOptions)
      } else {
        console.warn('户型配置不存在，使用默认选项')
        commit('SET_LAYOUTS', DEFAULT_LAYOUTS)
      }
      
      return { success: true, usingDefaults: false }
    } catch (error) {
      console.error('获取房屋配置失败，使用默认选项:', error)
      commit('SET_HOUSE_TYPES', DEFAULT_HOUSE_TYPES)
      commit('SET_LAYOUTS', DEFAULT_LAYOUTS)
      return { success: true, usingDefaults: true }
    }
  },
  
  async createConfig({ commit }, data) {
    try {
      const response = await apiCreateConfig(data)
      
      if (response.code === 200) {
        commit('ADD_CONFIG', response.data)
        
        // 记录操作日志
        Logger.recordConfigAction('创建', response.data, data, { success: true })
        
        return { success: true, ...response }
      } else {
        console.error('创建配置失败:', response)
        throw new Error(response.msg || '创建配置失败')
      }
    } catch (error) {
      console.error('创建配置失败:', error)
      throw error
    }
  },
  
  async updateConfig({ commit }, { id, data }) {
    try {
      const response = await apiUpdateConfig(id, data)
      
      if (response.code === 200) {
        commit('UPDATE_CONFIG_IN_LIST', response.data)
        commit('SET_CURRENT_CONFIG', response.data)
        
        // 记录操作日志
        Logger.recordConfigAction('更新', response.data, data, { success: true })
        
        return { success: true, ...response }
      } else {
        console.error('更新配置失败:', response)
        throw new Error(response.msg || '更新配置失败')
      }
    } catch (error) {
      console.error('更新配置失败:', error)
      throw error
    }
  },
  
  async deleteConfig({ commit, state }, id) {
    try {
      const config = state.configList.find(config => config.id === id)
      const response = await apiDeleteConfig(id)
      
      if (response.code === 200) {
        commit('REMOVE_CONFIG_FROM_LIST', id)
        
        // 记录操作日志
        if (config) {
          Logger.recordConfigAction('删除', config, { id }, { success: true })
        }
        
        return { success: true, ...response }
      } else {
        console.error('删除配置失败:', response)
        throw new Error(response.msg || '删除配置失败')
      }
    } catch (error) {
      console.error('删除配置失败:', error)
      throw error
    }
  }
}

const getters = {
  provinceOptions(state) {
    const options = state.locationOptions.provinces.length > 0 
      ? state.locationOptions.provinces 
      : DEFAULT_PROVINCES
    return formatOptions(options)
  },
  
  cityOptions(state) {
    const options = state.locationOptions.cities.length > 0 
      ? state.locationOptions.cities 
      : DEFAULT_CITIES
    return formatOptions(options)
  },
  
  districtOptions(state) {
    const options = state.locationOptions.districts.length > 0 
      ? state.locationOptions.districts 
      : DEFAULT_DISTRICTS
    return formatOptions(options)
  },
  
  houseTypeOptions(state) {
    const options = state.houseTypes.length > 0 
      ? state.houseTypes 
      : DEFAULT_HOUSE_TYPES
    // 直接格式化，不添加"全部"选项，因为配置中已包含"不限"
    return options.map(opt => ({
      label: opt,
      value: opt === '不限' ? '' : opt // "不限"对应空值
    }))
  },
  
  houseTypeOptionsWithoutAll(state) {
    const options = state.houseTypes.length > 0 
      ? state.houseTypes 
      : DEFAULT_HOUSE_TYPES
    // 过滤掉"不限"选项，用于编辑表单
    return options
      .filter(opt => opt !== '不限')
      .map(opt => ({
        label: opt,
        value: opt
      }))
  },
  
  layoutOptions(state) {
    const options = state.layouts.length > 0 
      ? state.layouts 
      : DEFAULT_LAYOUTS
    // 直接格式化，"全部"对应空值
    return options.map(opt => ({
      label: opt,
      value: opt === '全部' ? '' : opt
    }))
  }
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
}